import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { PaginatedRepairsResponse } from '../../models/repair.model';
import { map } from 'rxjs';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { Repair } from '../../models/response.model';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-casing-repair-create',
  templateUrl: './casing-credit-repair-create.component.html',
  styleUrl: './casing-credit-repair-create.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditRepairCreateComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: false }) tableRef: TableComponent<Repair>;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(Router);

  // Form related properties
  createForm: FormGroup;
  repairRowsForm: FormGroup; // Form to track repair amount values
  initialFormValues: any = {};
  isLoading = false;

  // Table related properties
  repairs: Repair[] = [];
  allRepairs: Map<string, number>;
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;

  tableConfig: TableConfig<Repair> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<Repair> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    const repairsArray = this.repairRowsForm.get('repairs') as FormArray;
    // Loop through the repairs and create repair detail objects
    this.repairs.forEach((repair, index) => {
      if (index < repairsArray.length) {
        const amountValue = repairsArray.at(index).value;
        const amount = Number(amountValue) || 0;
        if (amount > 0) {
          // Only add/update repairs with non-zero amounts
          if (
            !this.allRepairs.has(repair.repairPk) ||
            (this.allRepairs.has(repair.repairPk) && this.allRepairs.get(repair.repairPk) !== amount)
          ) {
            this.allRepairs.set(repair.repairPk, amount);
          }
        } else if (this.allRepairs.has(repair.repairPk)) {
          this.allRepairs.delete(repair.repairPk);
        }
      }
    });
    return this.productDataService.getAllPaginatedRepairs(filters.page, filters.limit).pipe(
      map((response: PaginatedRepairsResponse) => {
        this.repairs = response.data.repairs || [];
        this.pagination = response.data.pagination;
        // Update form controls for amount fields
        this.updateRepairRowsForm();
        return { items: this.repairs, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.createForm = this.fb.group({
      code: ['', Validators.required],
      description: ['', Validators.required]
    });
    // Initialize repair rows form
    this.repairRowsForm = this.fb.group({
      repairs: this.fb.array([])
    });
  }

  ngOnInit(): void {
    // Store initial form values for change detection
    // Initialize the allRepairs Map
    this.allRepairs = new Map<string, number>();
    this.initialFormValues = this.createForm.value;

    // Configure table columns
    this.configureColumns();

    // Update the table when language changes
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.configureColumns();
      this.cdr.markForCheck();
    });
  }

  /**
   * Custom validator to validate amount format and range
   * - Must not exceed 999.99
   * - Must have valid decimal format (max 2 decimal places)
   */
  private amountValidator() {
    return (control: FormControl) => {
      const value = control.value;

      if (value === null || value === undefined || value === '') {
        return null; // Allow empty values
      }

      const numValue = Number(value);

      // Check if it's a valid number
      if (isNaN(numValue)) {
        return { invalidNumber: true };
      }

      // Check if it exceeds maximum
      if (numValue > 999.99) {
        return { maxAmount: { max: 999.99, actual: numValue } };
      }

      return null;
    };
  }

  /**
   * Format amount for display (e.g., 0 becomes "0.00")
   * Also validates that the value doesn't exceed 999.99
   */
  private formatAmountForDisplay(amount: number): string {
    if (amount === null || amount === undefined || amount === 0) {
      return '0.00';
    }
    const numAmount = Number(amount);
    // Validate that the value doesn't exceed max allowed
    if (numAmount > 999.99) {
      return '0.00'; // Default to 0.00 if invalid
    }
    return numAmount.toFixed(2);
  }

  /**
   * Format amount for saving (ensures proper decimal format)
   * If integer like 3, converts to 3.00; if decimal like 5.20, keeps as 5.20
   */
  private formatAmountForSave(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0.0;
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return 0.0;
    }
    // Return the number rounded to 2 decimal places
    return Math.round(numValue * 100) / 100;
  }

  /**
   * Creates a form control for a repair row
   */
  createRepairFormControl(amount: number = 0): FormControl {
    const formattedAmount = this.formatAmountForDisplay(amount);
    return this.fb.control(formattedAmount, [Validators.required, this.amountValidator()]);
  }

  /**
   * Gets the form control for a specific repair item
   */
  getControl(item: Repair): FormControl {
    const index = this.repairs.findIndex((repair) => repair.repairPk === item.repairPk);
    if (index === -1) {
      return this.createRepairFormControl();
    }
    // If the form array doesn't have an entry for this repair yet, add one
    const repairsArray = this.repairRowsForm.get('repairs') as FormArray;
    while (repairsArray.length <= index) {
      repairsArray.push(this.createRepairFormControl());
    }
    return repairsArray.at(index) as FormControl;
  }

  /**
   * Updates the repair rows form when repairs data is loaded
   */
  updateRepairRowsForm(): void {
    const repairsArray = this.fb.array(
      this.repairs.map((repair) =>
        this.createRepairFormControl(this.allRepairs && this.allRepairs.has(repair.repairPk) ? this.allRepairs.get(repair.repairPk) : 0)
      )
    );
    this.repairRowsForm.setControl('repairs', repairsArray);
    this.cdr.markForCheck();
  }

  private configureColumns(): void {
    this.tableConfig.columns = {
      code: {
        cell: (elm: Repair) => elm.code,
        header: this.translationService.translate('codeText') || 'Code'
      },
      description: {
        cell: (elm: Repair) => elm.description,
        header: this.translationService.translate('descriptionText') || 'Description'
      },
      amount: {
        cell: 'amount', // This references the custom cell template in the HTML
        header: this.translationService.translate('AmountText') || 'Amount'
      }
    };
  }

  saveChanges(): void {
    if (this.createForm.invalid) {
      return;
    }

    this.isLoading = true;

    // Get form data
    const formData = this.createForm.value;
    const repairsData = [];
    // Get repair details from the form values
    const repairsArray = this.repairRowsForm.get('repairs') as FormArray;
    // Loop through the repairs and create repair detail objects
    this.repairs.forEach((repair, index) => {
      if (index < repairsArray.length) {
        const amount = this.formatAmountForSave(repairsArray.at(index).value);
        repairsData.push({
          repairFk: repair.repairPk,
          code: repair.code,
          description: repair.description,
          amount: amount
        });
      }
    });
    this.allRepairs.forEach((value, key) => {
      if (!repairsData.find((r) => r.repairFk === key)) {
        repairsData.push({
          repairFk: key,
          code: '',
          description: '',
          amount: this.formatAmountForSave(value)
        });
      }
    });
    const requestData = {
      code: formData.code,
      description: formData.description,
      repairDetails: repairsData
    };

    // Call the API
    this.productDataService
      .saveCasingCreditRepair(requestData)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          // Update the initial form values after successful save to avoid unsaved changes popup
          this.initialFormValues = { ...this.createForm.value };

          // Clear the allRepairs Map since they're now saved
          this.allRepairs.clear();

          // Update form state
          const repairsArray = this.repairRowsForm.get('repairs') as FormArray;
          if (repairsArray) {
            for (let i = 0; i < repairsArray.length; i++) {
              repairsArray.at(i).setValue('0.00');
              repairsArray.at(i).markAsPristine();
            }
          }

          this.createForm.markAsPristine();

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('casingCreditRepairCreatedSuccessfullyText')
          } as Alert);
          this.navigateBack();
        },
        error: (error) => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: error.message || this.translationService.translate('somethingWentWrongText') || 'Error creating casing credit repair'
          } as Alert);
        }
      });
  }

  navigateBack(): void {
    this.router.navigate(['/home/productData/casing-CreditRepair']);
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to 3 integer digits and 2 decimal digits (XXX.XX format).
   * Auto-inserts decimal point after 3 digits.
   * Prevents entry beyond the limit (does not auto-cap).
   * Preserves caret position and keeps FormControl in sync.
   */
  restrictTwoDecimalPlaces(event: Event, item: Repair): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, ''); // remove invalid chars
      v = v.replace(/(\..*)\./g, '$1'); // keep only first dot

      if (v === '.') {
        return '0.';
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }

      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        // Limit integer part to 3 digits
        if (intPart.length > 3) {
          // Reject if trying to enter more than 3 digits before decimal
          return null; // Return null to indicate invalid
        }
        // Limit decimal to 2 digits
        let limitedFracPart = fracPart.slice(0, 2);
        v = intPart + '.' + limitedFracPart;
      } else {
        // If no decimal, limit to 3 digits max
        if (v.length > 3) {
          // Reject if trying to enter more than 3 digits
          return null; // Return null to indicate invalid
        }
        // Auto-insert decimal after 3 digits (but NOT when deleting)
        if (v.length === 3 && !isDeleting) {
          v = v + '.';
        }
        // remove leading zeros only if followed by another digit (so "0" stays "0")
        v = v.replace(/^0+(\d)/, '$1');
      }

      // Final validation: ensure the numeric value doesn't exceed 999.99
      const numVal = Number(v);
      if (!isNaN(numVal) && numVal > 999.99) {
        return null; // Reject if exceeds maximum
      }

      return v;
    };

    let newValue = cleanValue(originalValue);

    // If cleanValue returned null, it means the input tried to exceed the limit - reject it
    if (newValue === null) {
      // Prevent the change by resetting to the original value
      input.value = originalValue;
      return;
    }

    // If deletion intent and the cleaned value is '0', allow it to become empty so backspace can clear.
    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (cleanedPrefix === null) {
      cleanedPrefix = originalPrefix;
    }
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    // Clear deletion flag so it doesn't affect next non-deleting input
    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    if (newValue === originalValue) {
      try {
        // ignore if nothing to sync
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const control = this.getControl(item);
      if (control) {
        // Allow empty string during editing - don't force '0.00'
        control.setValue(newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle blur event to validate and format the amount
   * Rejects values > 999.99 and formats to 2 decimals
   */
  onAmountBlur(event: Event, item: Repair): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const value = input.value?.trim();

    // If field is empty on blur, set to '0.00'
    if (!value || value === '') {
      input.value = '0.00';
      try {
        const control = this.getControl(item);
        if (control) {
          control.setValue('0.00', { emitEvent: false });
        }
      } catch {
        // ignore
      }
    } else {
      // Validate and format the value
      const numValue = Number(value);

      // Check if it's a valid number
      if (isNaN(numValue)) {
        // Invalid number, reset to 0.00
        input.value = '0.00';
        try {
          const control = this.getControl(item);
          if (control) {
            control.setValue('0.00', { emitEvent: false });
          }
        } catch {
          // ignore
        }
      } else if (numValue > 999.99) {
        // Value exceeds maximum, reject and reset to 0.00
        input.value = '0.00';
        try {
          const control = this.getControl(item);
          if (control) {
            control.setValue('0.00', { emitEvent: false });
          }
        } catch {
          // ignore
        }
      } else {
        // Valid value, format to 2 decimals
        const formattedValue = numValue.toFixed(2);
        input.value = formattedValue;
        try {
          const control = this.getControl(item);
          if (control) {
            control.setValue(formattedValue, { emitEvent: false });
          }
        } catch {
          // ignore
        }
      }
    }
    this.cdr.markForCheck();
  }

  isFormChanged(): boolean {
    // Check if main form fields have changed
    if (this.createForm && this.initialFormValues) {
      const currentFormValues = this.createForm.value;
      if (currentFormValues.code !== this.initialFormValues.code || currentFormValues.description !== this.initialFormValues.description) {
        return true;
      }
    }

    // Check if any amount values have been entered in the repair rows
    if (this.repairRowsForm) {
      const repairsArray = this.repairRowsForm.get('repairs') as FormArray;
      if (repairsArray) {
        for (let i = 0; i < repairsArray.length; i++) {
          const amountValue = repairsArray.at(i).value;
          const amount = Number(amountValue) || 0;
          // If any amount is greater than 0, consider the form changed
          if (amount > 0) {
            return true;
          }
        }
      }
    }

    return false;
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(
      this.isFormChanged.bind(this),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }
}
